import { trackActionInvoked } from "@/object-actions/trackObjectAction";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { FileRecord } from "@cohesive/api-client";
import { parentFolderPath } from "./paths";

export interface TransferSubmenuCommits {
  commitCopy: (destination: TransferDestination) => void;
  commitMove: (destination: TransferDestination) => void;
}

/**
 * Decorates resolved `move` / `copy-to` actions with an inline
 * workspace → folder destination submenu (mirrors the
 * `attachFileRecordDownloadVariants` pattern). The actions' existing handlers
 * are kept as fallbacks for surfaces that can't host a cascading submenu
 * (e.g. the canvas floating-toolbar utility menu), so this is purely additive.
 */
export function attachFileRecordTransferSubmenus(
  actions: ResolvedObjectAction[],
  record: FileRecord | null,
  commits: TransferSubmenuCommits,
): ResolvedObjectAction[] {
  if (!record?.id) {
    return actions;
  }

  const isFolder = record.path?.endsWith("/") ?? false;
  const currentWorkspaceId =
    record.class === "workspace" ? record.workspace_id : undefined;
  const objectSubtype = isFolder ? `${record.class}:folder` : record.class;

  return actions.map((action) => {
    if (action.id === "copy-to") {
      const submenu: ObjectActionDestinationSubmenu = {
        kind: "workspace-destination",
        operation: "copy",
        commit: (destination) => {
          trackActionInvoked("copy-to", {
            objectKind: "file-record",
            objectSubtype,
          });
          commits.commitCopy(destination);
        },
      };
      return { ...action, submenu };
    }

    if (action.id === "move") {
      const submenu: ObjectActionDestinationSubmenu = {
        kind: "workspace-destination",
        operation: "move",
        commit: (destination) => {
          trackActionInvoked("move", {
            objectKind: "file-record",
            objectSubtype,
          });
          commits.commitMove(destination);
        },
        currentWorkspaceId,
        currentFolderPath: currentWorkspaceId
          ? parentFolderPath(record.path)
          : undefined,
        forbiddenPathPrefixes:
          isFolder && record.path ? [record.path] : undefined,
      };
      return { ...action, submenu };
    }

    return action;
  });
}
